// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-COST-6 — manual / recurring lines, one panel for both sides of the P&L:
//   mode 'cost'   — "Other costs": anything Owl pays that is not Azure (GitHub seats, Claude, ClickUp, domains…)
//   mode 'income' — "Income & credits": setup fees, extra billing (+) and credits / discounts (−), optionally per customer
// Monthly lines continue until cut off; one month can be changed or skipped. Every action is audit-logged.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import { Plus } from 'lucide-react'
import ActionMenu, { type MenuAction } from '../../../components/ui/ActionMenu'
import AppButton from '../../../components/ui/AppButton'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'
import StatusChip from '../../../components/ui/StatusChip'
import { useToast } from '../../../components/ui/Toast'
import { DataGrid, type DataGridColumn } from '../../../components/DataGrid'
import { friendlyError } from '../../../api/errors'
import {
  createCostItem, cutOffCostItem, financeKeys, listCostItems, removeCostItem, resetCostItemMonth, setCostItemMonth,
  updateCostItem, type CostItemInput, type FinanceOtherLine,
} from '../../../api/finance'
import { CADENCE_LABEL, CostItemDialog, CutOffDialog, MonthAmountDialog, monthLabel, type LineMode } from './CostItemDialogs'
import { money } from './FinanceTables'

type Dialog = { kind: 'add' } | { kind: 'edit' | 'month' | 'cutoff' | 'remove'; line: FinanceOtherLine } | null

export default function OtherCostsPanel({ month, lines, mode = 'cost', customers = [] }: {
  month: string; lines: FinanceOtherLine[]; mode?: LineMode; customers?: { id: string; name: string }[]
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const [dialog, setDialog] = useState<Dialog>(null)
  const items = useQuery({ queryKey: financeKeys.items, queryFn: listCostItems, enabled: dialog?.kind === 'edit' })

  const run = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: financeKeys.all }); setDialog(null) },
  })
  /** Menu actions without a dialog report failure as a toast; dialog actions show it inside the dialog. */
  const act = (fn: () => Promise<unknown>, done: string, inDialog = true) => {
    run.reset()
    run.mutate(fn, { onSuccess: () => toast.show(done, 'success'), onError: e => { if (!inDialog) toast.show(friendlyError(e), 'error') } })
  }
  const error = run.isError ? friendlyError(run.error) : null
  const close = () => { if (!run.isPending) { setDialog(null); run.reset() } }
  const open = (d: Dialog) => { run.reset(); setDialog(d) }

  const actionsFor = (l: FinanceOtherLine): MenuAction[] => {
    const a: MenuAction[] = [{ key: 'edit', label: 'Edit…', hint: 'Changes every month', onClick: () => open({ kind: 'edit', line: l }) }]
    if (l.skipped || l.overridden)
      a.push({ key: 'undo', label: l.skipped ? 'Undo skip' : 'Undo change', hint: `Back to ${money(l.base_usd)} in ${monthLabel(month)}`,
        onClick: () => act(() => resetCostItemMonth(l.item_id, month), `${l.title}: ${monthLabel(month)} back to normal`, false) })
    else
      a.push(
        { key: 'month', label: 'Change this month…', hint: `${monthLabel(month)} only`, onClick: () => open({ kind: 'month', line: l }) },
        { key: 'skip', label: 'Skip this month', hint: `$0 in ${monthLabel(month)} only`,
          onClick: () => act(() => setCostItemMonth(l.item_id, month, { skipped: true }), `${l.title}: skipped ${monthLabel(month)}`, false) })
    if (l.cadence !== 'one_off')
      a.push(l.end_month
        ? { key: 'resume', label: 'Resume', hint: 'Remove the last month — continues again',
            onClick: () => act(() => cutOffCostItem(l.item_id, null), `${l.title} continues every month again`, false) }
        : { key: 'cutoff', label: 'Cut off…', hint: 'Stop it from a month on', onClick: () => open({ kind: 'cutoff', line: l }) })
    a.push({ key: 'remove', label: 'Remove…', hint: 'Only for a cost entered by mistake', danger: true, dividerBefore: true,
      onClick: () => open({ kind: 'remove', line: l }) })
    return a
  }

  const cols: DataGridColumn<FinanceOtherLine>[] = [
    {
      key: 'title', header: 'Cost', minWidth: 200, value: l => l.title,
      render: l => <Box><b>{l.title}</b>{l.vendor && <Box component="span" sx={{ color: 'text.secondary' }}> · {l.vendor}</Box>}</Box>,
    },
    { key: 'category', header: 'Category', value: l => l.category },
    ...(mode === 'income' ? [{
      key: 'kind', header: 'Type / customer', value: (l: FinanceOtherLine) => `${l.kind} ${l.customer_name ?? ''}`,
      render: (l: FinanceOtherLine) => (
        <Box>
          {l.kind === 'credit' ? <StatusChip status="credit" tone="amber" label="Credit (−)" /> : <StatusChip status="income" tone="green" label="Income (+)" />}
          <Box sx={{ fontSize: 11.5, color: 'text.secondary' }}>{l.customer_name ?? 'not tied to a customer'}</Box>
        </Box>
      ),
    } as DataGridColumn<FinanceOtherLine>] : []),
    {
      key: 'cadence', header: 'How often', value: l => CADENCE_LABEL[l.cadence],
      render: l => (
        <Box>
          {CADENCE_LABEL[l.cadence]}
          <Box sx={{ fontSize: 11.5, color: 'text.secondary' }}>
            {l.cadence === 'one_off' ? monthLabel(l.start_month)
              : `${monthLabel(l.start_month)} → ${l.end_month ? monthLabel(l.end_month) : 'continues'}`}
          </Box>
        </Box>
      ),
    },
    {
      key: 'status', header: 'This month', noFilter: true, value: l => (l.skipped ? 'skipped' : l.overridden ? 'changed' : 'normal'),
      render: l => l.skipped ? <StatusChip status="skipped" tone="grey" label="Skipped" />
        : l.overridden ? <StatusChip status="changed" tone="amber" label={`Changed from ${money(l.base_usd)}`} />
        : l.end_month === month && l.cadence !== 'one_off' ? <StatusChip status="last" tone="amber" label="Last month" />
        : <StatusChip status="normal" tone="green" label="Counted" />,
    },
    {
      key: 'amount', header: 'Amount', align: 'right', value: l => (l.kind === 'credit' ? -l.amount_usd : l.amount_usd),
      render: l => <b>{l.kind === 'credit' ? `−${money(l.amount_usd)}` : money(l.amount_usd)}</b>,
    },
    { key: 'actions', header: '', noFilter: true, width: 56, render: l => <ActionMenu label={`Actions for ${l.title}`} actions={actionsFor(l)} /> },
  ]

  const editing = dialog?.kind === 'edit' ? items.data?.items.find(i => i.id === dialog.line.item_id) ?? null : null

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ fontWeight: 700, fontSize: 13 }}>{mode === 'cost' ? 'Other costs' : 'Income & credits'} — {monthLabel(month)}</Box>
        <AppButton size="small" variant="contained" startIcon={<Plus size={14} />} onClick={() => open({ kind: 'add' })}>
          {mode === 'cost' ? 'Add cost' : 'Add income or credit'}
        </AppButton>
      </Box>
      {lines.length === 0
        ? <Box sx={{ fontSize: 12.5, color: 'text.secondary', py: 1.5 }}>
            {mode === 'cost'
              ? <>No other costs in {monthLabel(month)}. Add GitHub seats, Claude, ClickUp, domains, courier fees — anything Owl pays for.</>
              : <>No other income or credits in {monthLabel(month)}. Add a setup fee, extra billing, or a credit / discount given to a customer.</>}
          </Box>
        : <DataGrid rows={lines} columns={cols} storageKey={mode === 'cost' ? 'finance-v2-other' : 'finance-v2-income'} getRowKey={l => l.item_id} dense />}

      <CostItemDialog mode={mode} customers={customers}
        open={dialog?.kind === 'add' || (dialog?.kind === 'edit' && !!editing)} initial={editing} defaultMonth={month}
        busy={run.isPending} error={error ?? (dialog?.kind === 'edit' && items.isError ? friendlyError(items.error) : null)} onClose={close}
        onSave={(input: CostItemInput) => dialog?.kind === 'edit' && editing
          ? act(() => updateCostItem(editing.id, input), `${input.title} updated`)
          : act(() => createCostItem(input), `${input.title} added`)} />

      {dialog?.kind === 'month' && (
        <MonthAmountDialog open title={dialog.line.title} month={month} current={dialog.line.base_usd} busy={run.isPending} error={error}
          onClose={close} onSave={(amount, reason) => act(() => setCostItemMonth(dialog.line.item_id, month, { skipped: false, amount_usd: amount, reason }),
            `${dialog.line.title}: ${monthLabel(month)} set to ${money(amount)}`)} />
      )}
      {dialog?.kind === 'cutoff' && (
        <CutOffDialog open title={dialog.line.title} startMonth={dialog.line.start_month} defaultMonth={month} busy={run.isPending} error={error}
          onClose={close} onSave={end => act(() => cutOffCostItem(dialog.line.item_id, end), `${dialog.line.title} stops after ${monthLabel(end)}`)} />
      )}
      <ConfirmDialog open={dialog?.kind === 'remove'} tone="danger" title={`Remove ${dialog?.kind === 'remove' ? dialog.line.title : ''}?`}
        body="It disappears from every month. To stop a real cost from a month on, use Cut off instead — that keeps the history."
        confirmLabel="Remove" busy={run.isPending} error={error} onCancel={close}
        onConfirm={() => { if (dialog?.kind === 'remove') { const l = dialog.line; act(() => removeCostItem(l.item_id), `${l.title} removed`) } }} />
    </Box>
  )
}
